import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Alert, Badge } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Select from 'react-select';
import { brandSelectTheme } from '../Common/reactSelectTheme';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { getImportPresets } from '../../../services/MatchingService';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Landmark,
  RefreshCw
} from 'lucide-react';

/**
 * Bank statement CSV import (Phase 1b consolidated flow, step 2).
 *
 * The bank picker is fed by GET /import/presets — the backend's actual
 * column-mapping presets — and the chosen preset name is sent as a
 * MULTIPART FORM FIELD next to the file (the backend reads
 * req.body.preset). The old ?bankName= query contract silently parsed
 * everything as the Swedbank default (audit E1).
 *
 * Import stats stay on screen until the user clicks the explicit "Klar"
 * button (audit E3 — no auto-close over the numbers).
 */
const BankImportModal = ({ show, onHide, onImportSuccess }) => {
  const { companyId } = useParams();

  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetsError, setPresetsError] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);

  const [file, setFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const fetchPresets = useCallback(async () => {
    setPresetsLoading(true);
    setPresetsError(null);
    try {
      const data = await getImportPresets(companyId);
      setPresets(data?.presets || []);
    } catch (err) {
      console.error('Error fetching import presets:', err);
      setPresetsError('Kunde inte hämta bankformat. Försök igen.');
    } finally {
      setPresetsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (show) {
      fetchPresets();
    }
  }, [show, fetchPresets]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setFileError(null);
    setImportResult(null);

    if (rejectedFiles && rejectedFiles.length > 0) {
      setFileError('Endast CSV-filer är tillåtna. Välj en fil som slutar med .csv');
      setFile(null);
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];

      if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
        setFileError('Filen måste vara en CSV-fil (.csv)');
        setFile(null);
        return;
      }

      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedPreset) {
      setFileError('Välj din banks filformat i listan.');
      return;
    }
    if (!file) {
      setFileError('Välj en CSV-fil för att importera.');
      return;
    }

    setLoading(true);
    // Preset travels as a multipart form field — the backend reads
    // req.body.preset (never a query param, audit E1).
    const formData = new FormData();
    formData.append('file', file);
    formData.append('preset', selectedPreset.value);

    try {
      const apiUrl = getApiUrl(`${companyId}/accounting/reconciliation/banking/import`);
      const response = await axios.post(apiUrl, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
    } catch (error) {
      console.error('Error importing bank statement:', error);
      setImportResult({
        success: false,
        error: error.response?.data?.message || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setFile(null);
    setSelectedPreset(null);
    setImportResult(null);
    setFileError(null);
  };

  const handleClose = () => {
    if (!loading) {
      resetState();
      onHide();
    }
  };

  // Explicit "Klar" after a successful import — the parent refetches and
  // can toast the auto-matching outcome (the import response carries a
  // `matching: { mode, summary }` block since import auto-runs the engine).
  const handleDone = () => {
    const result = importResult;
    resetState();
    if (onImportSuccess) {
      onImportSuccess(result);
    } else {
      onHide();
    }
  };

  const presetOptions = presets.map((preset) => ({
    value: preset.name,
    label: preset.label || preset.name,
    unverified: !!preset.unverified,
  }));

  const formatPresetOption = (option) => (
    <span className="d-inline-flex align-items-center gap-2">
      {option.label}
      {option.unverified && (
        <Badge bg="warning" text="dark" style={{ fontSize: '0.6875rem' }}>
          Ej verifierad
        </Badge>
      )}
    </span>
  );

  // Colors come from brandSelectTheme (Phase E) — only shape overrides here.
  // The old option-color override painted selections react-select blue.
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      border: '2px solid #e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        border: '2px solid #cbd5e0',
      },
    }),
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: 'var(--rgba-primary-1)',
              color: 'var(--primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Landmark size={20} />
          </div>
          <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
            Importera Banktransaktioner
          </span>
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={handleClose}
          disabled={loading}
          aria-label="Close"
        />
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        {/* Preset (bank format) selection */}
        <div className="mb-4">
          <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.875rem', color: '#374151' }}>
            <Landmark size={16} className="me-2" style={{ verticalAlign: 'middle' }} />
            Bankformat
          </label>
          {presetsError ? (
            <Alert variant="warning" className="d-flex align-items-center justify-content-between mb-0">
              <span>
                <AlertTriangle size={16} className="me-2" />
                {presetsError}
              </span>
              <Button size="sm" variant="outline-secondary" onClick={fetchPresets}>
                <RefreshCw size={14} className="me-1" />
                Försök igen
              </Button>
            </Alert>
          ) : (
            <>
              <Select
                options={presetOptions}
                theme={brandSelectTheme}
                value={selectedPreset}
                onChange={setSelectedPreset}
                formatOptionLabel={formatPresetOption}
                placeholder={presetsLoading ? 'Hämtar bankformat…' : 'Välj din banks filformat...'}
                styles={customSelectStyles}
                isLoading={presetsLoading}
                isDisabled={loading || presetsLoading}
                noOptionsMessage={() => 'Inga bankformat hittades'}
              />
              {selectedPreset?.unverified && (
                <div className="form-text" style={{ fontSize: '0.75rem' }}>
                  <AlertTriangle size={12} className="me-1" style={{ verticalAlign: 'middle', color: '#f59e0b' }} />
                  Formatet är byggt från bankens dokumentation och är ännu inte verifierat
                  mot en riktig exportfil — kontrollera resultatet extra noga.
                </div>
              )}
            </>
          )}
        </div>

        {/* File Error Alert */}
        {fileError && (
          <Alert variant="warning" className="mb-3">
            <AlertTriangle size={16} className="me-2" />
            {fileError}
          </Alert>
        )}

        {/* File Drop Zone */}
        <div className="mb-4">
          <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.875rem', color: '#374151' }}>
            <FileText size={16} className="me-2" style={{ verticalAlign: 'middle' }} />
            CSV-fil
          </label>
          <div
            {...getRootProps()}
            className={`border border-2 border-dashed rounded p-4 text-center ${
              isDragActive ? 'border-primary bg-light' : fileError ? 'border-danger' : 'border-secondary'
            }`}
            style={{ cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
          >
            <input {...getInputProps()} accept=".csv" disabled={loading} />
            <FileText
              size={48}
              className="mb-3"
              style={{ color: isDragActive ? '#3b82f6' : fileError ? '#dc3545' : '#6b7280' }}
            />
            {isDragActive ? (
              <p className="mb-0 text-primary" style={{ fontSize: '0.875rem' }}>
                Släpp CSV-filen här...
              </p>
            ) : (
              <div>
                <p className="mb-2" style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                  Dra och släpp en CSV-fil här, eller klicka för att välja
                </p>
                <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                  Accepterade format: .csv
                </p>
                {file && (
                  <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#f0fdf4' }}>
                    <p className="mb-0" style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '500' }}>
                      <CheckCircle size={14} className="me-2" style={{ verticalAlign: 'middle' }} />
                      {file.name}
                    </p>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                      Storlek: {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Import Result — stays visible until the user clicks "Klar" */}
        {importResult && (
          <div className="mb-3">
            {importResult.success ? (
              <Alert variant="success" className="mb-0">
                <div className="d-flex">
                  <CheckCircle size={20} className="me-3 mt-1" />
                  <div className="flex-grow-1">
                    <h6 className="mb-2" style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                      Import lyckades!
                    </h6>
                    <p className="mb-2" style={{ fontSize: '0.875rem' }}>
                      {importResult.message || `${importResult.statistics?.successful || 0} transaktioner importerade.`}
                    </p>
                    {importResult.statistics && (
                      <div style={{ fontSize: '0.75rem' }}>
                        <div className="d-flex gap-3 mb-1">
                          <span><strong>Total:</strong> {importResult.statistics.total}</span>
                          <span className="text-success"><strong>Lyckade:</strong> {importResult.statistics.successful}</span>
                        </div>
                        {importResult.statistics.duplicates > 0 && (
                          <div className="text-warning mb-1">
                            <strong>Dubbletter:</strong> {importResult.statistics.duplicates} (hoppades över)
                          </div>
                        )}
                        {importResult.statistics.failed > 0 && (
                          <div className="text-danger">
                            <strong>Misslyckades:</strong> {importResult.statistics.failed}
                          </div>
                        )}
                      </div>
                    )}
                    {importResult.errors && importResult.errors.length > 0 && (
                      <details className="mt-2">
                        <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#6b7280' }}>
                          Visa fel ({importResult.errors.length})
                        </summary>
                        <ul className="mb-0 mt-2" style={{ fontSize: '0.75rem' }}>
                          {importResult.errors.slice(0, 10).map((err, index) => (
                            <li key={index}>
                              Rad {err.row}: {err.message || err.error}
                            </li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li className="text-muted">... och {importResult.errors.length - 10} fler</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
              </Alert>
            ) : (
              <Alert variant="danger" className="mb-0">
                <div className="d-flex">
                  <AlertTriangle size={20} className="me-3 mt-1" />
                  <div>
                    <h6 className="mb-2" style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                      Import misslyckades
                    </h6>
                    <p className="mb-0" style={{ fontSize: '0.875rem' }}>
                      {importResult.error || importResult.message || 'Ett okänt fel uppstod'}
                    </p>
                  </div>
                </div>
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        {importResult?.success ? (
          <Button variant="primary" onClick={handleDone}>
            <CheckCircle size={16} className="me-2" />
            Klar
          </Button>
        ) : (
          <>
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Avbryt
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!file || !selectedPreset || loading}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
                  Importerar...
                </>
              ) : (
                <>
                  <Upload size={16} className="me-2" />
                  Importera
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};

export default BankImportModal;
